import { isEqual } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { targetingConstants } from 'sonata-platform-components';
import {
  Checkbox,
  Dot,
  Input,
  MultipleTagSelector,
  RadioButtonsGroup,
  Selector,
  Tag,
  Toast,
  type ValidationError
} from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { useTheme } from 'styled-components';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getTemperatureAndWeatherTags } from './helpers/getSectionHeaderTags.helper';
import { TemperatureAndWeatherStyled } from './TemperatureAndWeather.styled';

const { DEFAULT_TEMPERATURE_AND_WEATHER } = targetingConstants;

type State = {
  isAllTemperatures: boolean;
  isRiskAbove: boolean;
  showErrorToast: boolean;
};

type Props = {
  error?: ValidationError;
};

function TemperatureAndWeather({ error }: Props) {
  const { formatMessage } = useIntl();
  const { uiColors }: any = useTheme();

  const { setTemperatureAndWeatherProps, setUnknownIncluded, targeting, resetSection, user, userRoles } =
    useTargetingContext();

  const { temperatureAndWeather, unknownIncluded } = targeting;

  const unknownIncludedPermission = checkUserResourcePermissions('unknownIncluded', user, userRoles);

  const [{ isAllTemperatures, isRiskAbove, showErrorToast }, setState] = useState<State>({
    isAllTemperatures: !temperatureAndWeather?.temperature?.min && !temperatureAndWeather?.temperature?.max,
    isRiskAbove: !!temperatureAndWeather?.uv?.max,
    showErrorToast: true
  });

  const radioButtonGroupItems = [
    {
      id: 'all',
      text: formatMessage({
        defaultMessage: 'All Temperatures',
        id: 'targeting.temperatureAndWeather.radios.all'
      })
    },
    {
      id: 'bid',
      text: formatMessage({
        defaultMessage: 'Bid by Temperature',
        id: 'targeting.temperatureAndWeather.radios.bid'
      })
    }
  ];

  const selectorItems = [
    {
      id: 'metric',
      label: formatMessage({
        defaultMessage: 'Celsius',
        id: 'targeting.temperatureAndWeather.selectors.metric'
      })
    },
    {
      id: 'imperial',
      label: formatMessage({
        defaultMessage: 'Fahrenheit',
        id: 'targeting.temperatureAndWeather.selectors.imperial'
      })
    }
  ];

  const handleRadioGroupChange = (id: string) => {
    setTemperatureAndWeatherProps('temperature', {
      max: '',
      min: ''
    });
    id === 'all' && setUnknownIncluded('weatherTemperature', true);
    setState(prevState => ({
      ...prevState,
      isAllTemperatures: id === 'all'
    }));
  };

  const tagsItems = [
    {
      icon: { webFont: 'fa-regular fa-cloud-showers' },
      label: formatMessage({
        defaultMessage: 'Raining',
        id: 'targeting.temperatureAndWeather.tags.rain'
      }),
      value: 'rain'
    },
    {
      icon: { webFont: 'fa-regular fa-clouds' },
      label: formatMessage({
        defaultMessage: 'Cloudy',
        id: 'targeting.temperatureAndWeather.tags.cloud'
      }),
      value: 'cloud'
    },
    {
      icon: { webFont: 'fa-regular fa-sun-bright' },
      label: formatMessage({
        defaultMessage: 'Sunny',
        id: 'targeting.temperatureAndWeather.tags.clear'
      }),
      value: 'clear'
    },
    {
      icon: { webFont: 'fa-regular fa-cloud-fog' },
      label: formatMessage({
        defaultMessage: 'Foggy',
        id: 'targeting.temperatureAndWeather.tags.fog'
      }),
      value: 'fog'
    },
    {
      icon: { webFont: 'fa-regular fa-cloud-hail-mixed' },
      label: formatMessage({
        defaultMessage: 'Hailing',
        id: 'targeting.temperatureAndWeather.tags.hail'
      }),
      value: 'hail'
    },
    {
      icon: { webFont: 'fa-regular fa-snowflake' },
      label: formatMessage({
        defaultMessage: 'Snowing',
        id: 'targeting.temperatureAndWeather.tags.snow'
      }),
      value: 'snow'
    },
    {
      icon: { webFont: 'fa-regular fa-wind' },
      label: formatMessage({
        defaultMessage: 'Windy',
        id: 'targeting.temperatureAndWeather.tags.wind'
      }),
      value: 'wind'
    }
  ];

  const risksItems = [
    {
      classColor: 'var(--color-status-success-500, #65A990);',
      id: 'low',
      text: formatMessage({
        defaultMessage: 'Low Risk: 1, 2',
        id: 'targeting.temperatureAndWeather.risks.low'
      }),
      value: [1, 2]
    },
    {
      classColor: 'var(--color-Taptap-Brand-yellow, #FFE922);',
      id: 'medium',
      text: formatMessage({
        defaultMessage: 'Medium Risk: 3, 4, 5',
        id: 'targeting.temperatureAndWeather.risks.medium'
      }),
      value: [3, 4, 5]
    },
    {
      classColor: 'var(--color-status-warning-500, #FFB041);',
      id: 'high',
      text: formatMessage({
        defaultMessage: 'High Risk: 6, 7',
        id: 'targeting.temperatureAndWeather.risks.high'
      }),
      value: [6, 7]
    },
    {
      classColor: 'var(--color-status-error-550, #FB4868);',
      id: 'veryHigh',
      text: formatMessage({
        defaultMessage: 'Very High Risk: 8, 9',
        id: 'targeting.temperatureAndWeather.risks.veryHigh'
      }),
      value: [8, 9]
    },
    {
      classColor: 'var(--color-Taptap-Brand-black, #000);',
      id: 'extreme',
      text: formatMessage({
        defaultMessage: 'Extreme Risk: 10, 11+',
        id: 'targeting.temperatureAndWeather.risks.extreme'
      }),
      value: [10, 11]
    }
  ];

  const minUv = !!temperatureAndWeather?.uv?.min && parseInt(temperatureAndWeather.uv.min);
  const selectedRisk = minUv ? risksItems.find(item => item.value.includes(minUv)) : undefined;

  const isRiskItemSelected = (val: (typeof risksItems)[0]) => {
    if (!selectedRisk) {
      return false;
    }
    const selectedIndex = risksItems.findIndex(r => r.id === selectedRisk.id);
    const currentIndex = risksItems.findIndex(r => r.id === val.id);
    return isRiskAbove ? currentIndex >= selectedIndex : val.id === selectedRisk.id;
  };

  const handleRiskAboveChange = (val: boolean) => {
    setState(prevState => ({
      ...prevState,
      isRiskAbove: val
    }));

    selectedRisk &&
      setTemperatureAndWeatherProps('uv', {
        max: val ? '' : Math.max(...selectedRisk.value).toString(),
        min: temperatureAndWeather?.uv?.min
      });
  };

  const handleRiskTagClick = (id: string, value: number[]) => {
    if (selectedRisk?.id === id) {
      setState(prevState => ({ ...prevState, isRiskAbove: false }));
      setTemperatureAndWeatherProps('uv', { max: '', min: '' });
      return;
    }
    if (id === 'extreme') {
      setState(prevState => ({ ...prevState, isRiskAbove: true }));
    }
    setTemperatureAndWeatherProps('uv', {
      max: isRiskAbove || id === 'extreme' ? '' : Math.max(...value).toString(),
      min: Math.min(...value).toString()
    });
  };

  const handleResetTemperatureAndWeather = () => {
    setState(prevState => ({
      ...prevState,
      isAllTemperatures: true,
      isRiskAbove: false,
      showErrorToast: false
    }));
    resetSection('temperatureAndWeather');
  };

  const isSectionOpened = !isEqual(temperatureAndWeather, DEFAULT_TEMPERATURE_AND_WEATHER.temperatureAndWeather);
  const temperatureAndWeatherTags = getTemperatureAndWeatherTags(temperatureAndWeather);

  return (
    <TemperatureAndWeatherStyled className="temperatureAndWeather" id="temperatureAndWeatherBox">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        hasErrors={Boolean(error?.message)}
        onReset={handleResetTemperatureAndWeather}
        preffixId="temperatureAndWeather"
        tags={temperatureAndWeatherTags}
        title={formatMessage({
          defaultMessage: 'Temperature And Weather',
          id: 'targeting.temperatureAndWeather.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            "Trigger or pause your campaign based on real-time weather conditions or temperature ranges in the user's location.",
          id: 'targeting.temperatureAndWeather.tooltip'
        })}
      >
        <div>
          {error?.message && showErrorToast && (
            <Toast
              autoClose={false}
              className="settings__errorToast"
              closeToast={() => setState(prevState => ({ ...prevState, showErrorToast: false }))}
              icon="fal fa-exclamation-triangle"
              text={error?.message}
              type="danger"
            />
          )}
          <RadioButtonsGroup
            className="temperatureAndWeather__radioButtonsGroup"
            id={'temperatureAndWeatherRadioButtonList'}
            items={radioButtonGroupItems}
            onChange={handleRadioGroupChange}
            selectedItem={isAllTemperatures ? 'all' : 'bid'}
          />
          <div className="temperatureAndWeather__container">
            {!isAllTemperatures && (
              <div className="temperatureAndWeather__selectors">
                <div className="temperatureAndWeather__selectorsContainer">
                  <Input
                    defaultValue={temperatureAndWeather?.temperature?.min}
                    id={'temperatureAndWeatherInputFrom'}
                    inlineLabel
                    label={formatMessage({
                      defaultMessage: 'From',
                      id: 'targeting.temperatureAndWeather.selectors.from'
                    })}
                    name="temperatureAndWeatherInputFrom"
                    notFloatingPlaceholder
                    onChange={val =>
                      setTemperatureAndWeatherProps('temperature', {
                        max: temperatureAndWeather?.temperature?.max,
                        min: val
                      })
                    }
                    placeholder={formatMessage({
                      defaultMessage: 'Ex: 16',
                      id: 'targeting.temperatureAndWeather.selectors.placeholder'
                    })}
                    shape="square"
                    type="number"
                  />
                  <Input
                    defaultValue={temperatureAndWeather?.temperature?.max}
                    id={'temperatureAndWeatherInputTo'}
                    inlineLabel
                    label={formatMessage({
                      defaultMessage: 'To',
                      id: 'targeting.temperatureAndWeather.selectors.to'
                    })}
                    name="temperatureAndWeatherInputTo"
                    notFloatingPlaceholder
                    onChange={val =>
                      setTemperatureAndWeatherProps('temperature', {
                        max: val,
                        min: temperatureAndWeather?.temperature?.min
                      })
                    }
                    placeholder={formatMessage({
                      defaultMessage: 'Ex: 16',
                      id: 'targeting.temperatureAndWeather.selectors.placeholder'
                    })}
                    shape="square"
                    type="number"
                  />
                  <Selector
                    className="temperatureAndWeather__selectorDegreeType"
                    defaultValue={selectorItems.filter(item => item.id === temperatureAndWeather.temperatureScale)}
                    label={formatMessage({
                      defaultMessage: 'Degree type',
                      id: 'targeting.temperatureAndWeather.selectors.scale'
                    })}
                    onChange={(val: { id: string }) => setTemperatureAndWeatherProps('temperatureScale', val.id)}
                    options={selectorItems}
                    type="simple"
                  />
                </div>
                <div className="temperatureAndWeather__checks">
                  {unknownIncludedPermission && (
                    <Checkbox
                      defaultActive={unknownIncluded.weatherTemperature}
                      id={'temperatureAndWeatherCheckUnknownWeather'}
                      labelText={formatMessage({
                        defaultMessage: 'Target unknown',
                        id: 'targeting.temperatureAndWeather.checks.target'
                      })}
                      onClickAction={(_: string, checked: boolean) => setUnknownIncluded('weatherTemperature', checked)}
                      type="checkbox"
                    />
                  )}
                </div>
              </div>
            )}
            <div className="temperatureAndWeather__subtitle">
              {formatMessage({
                defaultMessage: 'Show Ads only when it is:',
                id: 'targeting.temperatureAndWeather.subtitles.ads'
              })}
            </div>
            <div className="temperatureAndWeather__contentTags">
              <MultipleTagSelector
                className="temperatureAndWeather__contentTagsMultiple"
                id={'temperatureAndWeather__multipleTag'}
                items={tagsItems}
                mark
                onChange={val => setTemperatureAndWeatherProps('weatherConditions', val)}
                selectedItems={temperatureAndWeather.weatherConditions}
                textColor={uiColors.temperatureAndWeatherIcon}
              />
              <div className="temperatureAndWeather__checks">
                {unknownIncludedPermission && (
                  <Checkbox
                    defaultActive={unknownIncluded.weatherConditions}
                    id={'temperatureAndWeatherCheckUnknownConditions'}
                    isSwitch={false}
                    labelText={formatMessage({
                      defaultMessage: 'Target unknown',
                      id: 'targeting.temperatureAndWeather.checks.target'
                    })}
                    onClickAction={(_: string, checked: boolean) => setUnknownIncluded('weatherConditions', checked)}
                    type="checkbox"
                  />
                )}
              </div>
            </div>
            <div className="temperatureAndWeather__subtitle">
              {formatMessage({
                defaultMessage: 'UV Index targeting options:',
                id: 'targeting.temperatureAndWeather.subtitles.uv'
              })}
            </div>
            <div className="temperatureAndWeather__risk">
              <div className="temperatureAndWeather__riskOptions">
                {risksItems.map(val => {
                  const isSelected = isRiskItemSelected(val);
                  return (
                    <div className="temperatureAndWeather__riskOptionsItem" key={val.id}>
                      <Dot color={val.classColor} label="" />
                      <Tag
                        id={`temperatureAndWeatherRiskTag${val.id}`}
                        onClick={() => handleRiskTagClick(val.id, val.value)}
                        status={isSelected ? 'enabled' : 'information'}
                      >
                        {val.text}
                      </Tag>
                    </div>
                  );
                })}
              </div>
              <div className="temperatureAndWeather__checks">
                <Checkbox
                  active={isRiskAbove}
                  disabled={selectedRisk?.id === 'extreme'}
                  id={'temperatureAndWeatherCheckAbove'}
                  isSwitch={false}
                  labelText={formatMessage({
                    defaultMessage: 'plus all of the above',
                    id: 'targeting.temperatureAndWeather.checks.above'
                  })}
                  onClickAction={(_: string, checked: boolean) => handleRiskAboveChange(checked)}
                  type="checkbox"
                />
                {unknownIncludedPermission && (
                  <Checkbox
                    defaultActive={unknownIncluded.weatherUvIndex}
                    id={'temperatureAndWeatherCheckUnknown'}
                    isSwitch={false}
                    labelText={formatMessage({
                      defaultMessage: 'Target unknown',
                      id: 'targeting.temperatureAndWeather.checks.target'
                    })}
                    onClickAction={(_: string, checked: boolean) => setUnknownIncluded('weatherUvIndex', checked)}
                    type="checkbox"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </FormExpandableSection>
    </TemperatureAndWeatherStyled>
  );
}

export default TemperatureAndWeather;
